import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';

const SellerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sellerData, setSellerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({}); // Add validation errors state

  useEffect(() => {
    if (id) {
      fetchSellerDetails();
    }
  }, [id]);

  const fetchSellerDetails = async () => {
    try {
      setLoading(true);
      
      const data = await apiService.get(`/seller-companies/${id}`);
      setSellerData(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching seller details:', err);
      setError('Failed to load seller details');
    } finally {
      setLoading(false);
    }
  };

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validation function
  const validateForm = () => {
    const errors = {};

    // Email validation
    if (sellerData.emailAddress && !isValidEmail(sellerData.emailAddress)) {
      errors.emailAddress = 'Please enter a valid email address';
    }

    // Contact number validation (max 20 characters)
    if (sellerData.contactNumber && sellerData.contactNumber.length > 20) {
      errors.contactNumber = 'Contact number must be 20 characters or less';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field, value) => {
    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors({
        ...validationErrors,
        [field]: undefined
      });
    }

    // Restrict contact number to 20 characters
    if (field === 'contactNumber' && value.length > 20) {
      return; // Don't update if exceeds 20 characters
    }

    setSellerData({
      ...sellerData,
      [field]: value
    });
  };

  const handleSave = async () => {
    // Validate form before submitting
    if (!validateForm()) {
      alert('Please fix the validation errors before saving.');
      return;
    }

    try {
      setSaving(true);

      const requestBody = {
        userId: 3,
        companyName: sellerData.companyName || "",
        description: sellerData.description || "Not captured", // Default to "Not captured"
        contactPerson: sellerData.contactPerson || "",
        contactNumber: sellerData.contactNumber || "",
        emailAddress: sellerData.emailAddress || ""
      };

      await apiService.put(`/seller-companies?id=${id}`, requestBody);
      
      await fetchSellerDetails();
      alert('Seller updated successfully!');
      
    } catch (err) {
      console.error('Error updating seller:', err);
      
      if (err.message.includes('Failed to fetch')) {
        alert('Network error: Unable to connect to the server. Please check your connection and try again.');
      } else if (err.message.includes('500')) {
        alert('Server error: The server encountered an internal error. Please try again in a moment.');
      } else {
        alert(`Failed to update seller: ${err.message}`);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="manage-properties">
        <div className="page-header">
          <h1>Seller Details</h1>
        </div>
        <div className="loading-state">
          <p>Loading seller details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="manage-properties">
        <div className="page-header">
          <h1>Seller Details</h1>
        </div>
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchSellerDetails} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!sellerData) {
    return (
      <div className="manage-properties">
        <div className="page-header">
          <h1>Seller Details</h1>
        </div>
        <div className="error-state">
          <p>Seller not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-properties">
      <div className="page-header">
        <h1>Seller Details</h1>
      </div>

      <div className="content-container">
        <div className="form-section">
          <h3>Company Information</h3>
          <div className="form-grid">
            <div className="form-row">
              <label htmlFor="companyName">Company Name:</label>
              <input
                type="text"
                id="companyName"
                className="form-input"
                value={sellerData.companyName || ''}
                onChange={(e) => handleInputChange('companyName', e.target.value)}
              />
            </div>
            
            <div className="form-row">
              <label htmlFor="contactPerson">Contact Person:</label>
              <input
                type="text"
                id="contactPerson"
                className="form-input"
                value={sellerData.contactPerson || ''}
                onChange={(e) => handleInputChange('contactPerson', e.target.value)}
              />
            </div>
            
            <div className="form-row">
              <label htmlFor="contactNumber">Contact Number:</label>
              <input
                type="text"
                id="contactNumber"
                className={`form-input ${validationErrors.contactNumber ? 'error' : ''}`}
                value={sellerData.contactNumber || ''}
                onChange={(e) => handleInputChange('contactNumber', e.target.value)}
                maxLength="20"
              />
              {validationErrors.contactNumber && (
                <span className="error-message">{validationErrors.contactNumber}</span>
              )}
              <small className="field-info">
                {sellerData.contactNumber ? `${sellerData.contactNumber.length}/20 characters` : '0/20 characters'}
              </small>
            </div>
            
            <div className="form-row">
              <label htmlFor="emailAddress">Email Address:</label>
              <input
                type="email"
                id="emailAddress"
                className={`form-input ${validationErrors.emailAddress ? 'error' : ''}`}
                value={sellerData.emailAddress || ''}
                onChange={(e) => handleInputChange('emailAddress', e.target.value)}
              />
              {validationErrors.emailAddress && (
                <span className="error-message">{validationErrors.emailAddress}</span>
              )}
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            className="btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SellerDetails;
